import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import client from "../api/client";
import AiFieldActions from "../components/AiFieldActions";
import SimpleTextEditor from "../components/SimpleTextEditor";
import AiAssistOptionsModal from "../components/AiAssistOptionsModal";
import NotesIngestModal from "../components/NotesIngestModal";

const SECTION_DEFS = [
  {
    key: "executive_summary",
    label: "Executive Summary",
    hint: "Keep each point short and client-ready."
  },
  {
    key: "positive_signals",
    label: "What Is Working Well",
    hint: "Capture strengths already present in the operating model so the client knows what should be preserved."
  },
  {
    key: "current_risks",
    label: "Current Risks and Negative Signals",
    hint: "List the operational, technical, and governance gaps that currently create instability, downtime, or preventable risk."
  },
  {
    key: "incident_learnings",
    label: "Incident Learnings",
    hint: "Explain what repeated incidents, downtime, and issue patterns are telling us about the way the website is being run."
  },
  {
    key: "process_findings_by_area",
    label: "Process Findings by Area",
    hint: "Break the process audit down by areas such as hosting, updates, ownership, release discipline, plugins, integrations, and support flow."
  },
  {
    key: "operational_readiness",
    label: "Operational Readiness",
    hint: "Assess recovery readiness, monitoring, backup confidence, access control, change governance, and accountability."
  },
  {
    key: "documentation_and_handover",
    label: "Documentation and Handover",
    hint: "Capture the state of access documentation, environment notes, recovery instructions, deployment knowledge, and business-critical handover material."
  },
  {
    key: "recommendations",
    label: "Recommendations",
    hint: "Write the practical improvements the client should make next, with a focus on stability, governance, and safer day-to-day operation."
  },
  {
    key: "recommendation_matrix",
    label: "Recommendation Matrix / Action Plan",
    hint: "Organize recommendations by area, priority, effort, impact, and suggested owner so the client can act on them."
  },
  {
    key: "consultant_notes",
    label: "Consultant Notes",
    hint: "Add your expert judgment, caveats, delivery notes, or supporting narrative that should stay with the final report."
  }
];

const EMPTY_SECTIONS = SECTION_DEFS.reduce((acc, section) => {
  acc[section.key] = "";
  return acc;
}, { risk_score: 0, section_meta: {} });

const GUIDED_SECTION_CONFIGS = {
  executive_summary: {
    mode: "list",
    itemLabel: "Executive Section",
    fields: [
      { key: "summary", label: "Summary", type: "textarea", placeholder: "Write a short client-ready statement for this heading." }
    ]
  },
  positive_signals: {
    mode: "list",
    itemLabel: "Positive Signal",
    fields: [
      { key: "signal", label: "Positive Signal", type: "textarea", placeholder: "What is already working well?" },
      { key: "area", label: "Area", type: "input", placeholder: "Governance, Platform, Security, Operations..." },
      { key: "why_it_matters", label: "Why It Matters", type: "textarea", placeholder: "Why should this be preserved or protected?" },
      { key: "rating", label: "Rating", type: "number", placeholder: "0-100" },
      { key: "status", label: "Status", type: "select", options: ["Strong", "Healthy", "Emerging"] }
    ]
  },
  current_risks: {
    mode: "list",
    itemLabel: "Risk Item",
    fields: [
      { key: "risk", label: "Risk", type: "textarea", placeholder: "Describe the current technical, process, or governance risk." },
      { key: "area", label: "Area", type: "input", placeholder: "Hosting, Governance, Security, Dependencies..." },
      { key: "business_impact", label: "Business Impact", type: "textarea", placeholder: "How could this affect uptime, trust, campaigns, or operations?" },
      { key: "recommended_action", label: "Recommended Action", type: "textarea", placeholder: "What should be done to reduce this risk?" },
      { key: "rating", label: "Rating", type: "number", placeholder: "0-100" },
      { key: "status", label: "Status", type: "select", options: ["Needs Improvement", "High Risk", "Critical"] }
    ]
  },
  incident_learnings: {
    mode: "fixed",
    items: [
      { key: "trigger_patterns", label: "Trigger Patterns" },
      { key: "recovery_gaps", label: "Recovery Gaps" },
      { key: "repeated_causes", label: "Repeated Causes" },
      { key: "monitoring_escalation", label: "Monitoring and Escalation" }
    ],
    fields: [
      { key: "observation", label: "Observation", type: "textarea", placeholder: "What did the incidents reveal in this area?" },
      { key: "learning", label: "Learning", type: "textarea", placeholder: "What should the client learn from this pattern?" },
      { key: "action", label: "Action", type: "textarea", placeholder: "What follow-up action should come from this learning?" },
      { key: "rating", label: "Rating", type: "number", placeholder: "0-100" },
      { key: "status", label: "Status", type: "select", options: ["Observed", "Needs Attention", "Critical"] }
    ]
  },
  process_findings_by_area: {
    mode: "fixed",
    items: [
      { key: "business_change", label: "Business and Change Context" },
      { key: "hosting_infrastructure", label: "Hosting and Infrastructure" },
      { key: "wordpress_application", label: "WordPress Application" },
      { key: "integrations_dependencies", label: "Integrations and Dependencies" },
      { key: "documentation_handover", label: "Documentation and Handover" }
    ],
    fields: [
      { key: "current_position", label: "Current Position", type: "textarea", placeholder: "Describe the current state for this area." },
      { key: "positive_signal", label: "Positive Signal", type: "textarea", placeholder: "What is working well here?" },
      { key: "process_risk", label: "Process Risk", type: "textarea", placeholder: "What is creating risk or instability?" },
      { key: "recommendation", label: "Recommendation", type: "textarea", placeholder: "What should be improved next?" },
      { key: "rating", label: "Rating", type: "number", placeholder: "0-100" },
      { key: "status", label: "Status", type: "select", options: ["Healthy", "Needs Improvement", "Critical"] }
    ]
  },
  recommendations: {
    mode: "fixed",
    items: [
      { key: "immediate_actions", label: "Immediate Actions" },
      { key: "near_term_actions", label: "Near-Term Actions" },
      { key: "structural_improvements", label: "Structural Improvements" },
      { key: "governance_improvements", label: "Governance Improvements" }
    ],
    fields: [
      { key: "recommendation", label: "Recommendation", type: "textarea", placeholder: "What should be done in this recommendation group?" },
      { key: "expected_outcome", label: "Expected Outcome", type: "textarea", placeholder: "What should improve after this action?" },
      { key: "suggested_owner", label: "Suggested Owner", type: "input", placeholder: "IT, Marketing, Vendor, Platform Owner..." },
      { key: "rating", label: "Rating", type: "number", placeholder: "0-100" },
      { key: "status", label: "Status", type: "select", options: ["Planned", "Priority", "Critical"] }
    ]
  },
  operational_readiness: {
    mode: "fixed",
    items: [
      { key: "backup_readiness", label: "Backup Readiness" },
      { key: "incident_response_readiness", label: "Incident Response Readiness" },
      { key: "release_readiness", label: "Release Readiness" },
      { key: "ownership_readiness", label: "Ownership Readiness" },
      { key: "documentation_readiness", label: "Documentation Readiness" }
    ],
    fields: [
      { key: "current_state", label: "Current State", type: "textarea", placeholder: "Describe the current readiness posture." },
      { key: "gap", label: "Gap", type: "textarea", placeholder: "What is missing, weak, or unclear?" },
      { key: "recommendation", label: "Recommendation", type: "textarea", placeholder: "What would strengthen readiness here?" },
      { key: "rating", label: "Rating", type: "number", placeholder: "0-100" },
      { key: "status", label: "Status", type: "select", options: ["Ready", "Partially Ready", "At Risk"] }
    ]
  },
  documentation_and_handover: {
    mode: "list",
    itemLabel: "Documentation Record",
    fields: [
      { key: "doc_type", label: "Documentation Type", type: "select", options: ["Access Documentation", "Environment Documentation", "Change and Release Documentation", "Recovery Documentation", "Dependency Documentation", "Business Notes", "Other"] },
      { key: "current_artifact", label: "Current Artifact", type: "textarea", placeholder: "What currently exists, where it is stored, and how usable it is." },
      { key: "owner", label: "Owner / Maintainer", type: "input", placeholder: "IT lead, vendor, marketing owner, platform owner..." },
      { key: "storage_location", label: "Storage Location", type: "input", placeholder: "SharePoint, password manager, internal wiki, Google Drive..." },
      { key: "gap", label: "Gap or Risk", type: "textarea", placeholder: "What is missing, outdated, inaccessible, or risky?" },
      { key: "recommendation", label: "Recommendation", type: "textarea", placeholder: "What documentation or handover improvement should happen next?" },
      { key: "rating", label: "Rating", type: "number", placeholder: "0-100" },
      { key: "status", label: "Status", type: "select", options: ["Documented", "Partial", "Missing", "Needs Review"] }
    ]
  },
  recommendation_matrix: {
    mode: "list",
    itemLabel: "Recommendation",
    fields: [
      { key: "recommendation", label: "Recommendation", type: "textarea", placeholder: "What action should be taken?" },
      { key: "area", label: "Area", type: "input", placeholder: "Governance, Infrastructure, Platform, Security..." },
      { key: "priority", label: "Priority", type: "select", options: ["Immediate", "Near Term", "Structural"] },
      { key: "impact", label: "Impact", type: "select", options: ["High", "Medium", "Low"] },
      { key: "effort", label: "Effort", type: "select", options: ["Low", "Medium", "High"] },
      { key: "suggested_owner", label: "Suggested Owner", type: "input", placeholder: "Marketing, IT, Vendor, Platform Owner..." },
      { key: "reason", label: "Reason / Expected Outcome", type: "textarea", placeholder: "Why this matters and what it should improve." }
    ]
  },
  consultant_notes: {
    mode: "fixed",
    items: [
      { key: "delivery_notes", label: "Delivery Notes" },
      { key: "caveats_assumptions", label: "Caveats and Assumptions" },
      { key: "follow_up_questions", label: "Follow-Up Questions" },
      { key: "consultant_judgment", label: "Consultant Judgment" }
    ],
    fields: [
      { key: "note", label: "Note", type: "textarea", placeholder: "Write the consultant note for this heading." },
      { key: "status", label: "Status", type: "select", options: ["Info", "Important", "Watch"] }
    ]
  }
};

function parseJson(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

function escapeRegex(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function createGuidedEntry(config, label = "") {
  return config.fields.reduce((acc, field) => {
    if (field.type === "select") {
      acc[field.key] = field.options?.[0] || "";
    } else if (field.type === "number") {
      acc[field.key] = "";
    } else {
      acc[field.key] = "";
    }
    return acc;
  }, { label });
}

function serializeGuidedSection(config, data) {
  const items = config.mode === "list"
    ? (data.items || [])
    : config.items.map((item) => ({ key: item.key, label: item.label, ...(data.items?.[item.key] || createGuidedEntry(config, item.label)) }));

  return items
    .map((item, index) => {
      const heading = config.mode === "list"
        ? item.label || `${config.itemLabel} ${index + 1}`
        : item.label;

      const body = config.fields
        .map((field) => `${field.label}:\n${String(item[field.key] ?? "")}`)
        .join("\n\n");

      return `### ${heading}\n${body}`;
    })
    .filter(Boolean)
    .join("\n\n");
}

function parseGuidedSection(config, rawText) {
  const text = String(rawText || "");
  const fieldLabels = config.fields.map((field) => escapeRegex(field.label)).join("|");
  const itemBlocks = text
    ? text.split(/\n(?=### )/).map((part) => part.replace(/^\n+|\n+$/g, "")).filter((part) => part.trim() !== "")
    : [];

  const extractFieldValue = (body, label) => {
    const pattern = new RegExp(`${escapeRegex(label)}:\\n([\\s\\S]*?)(?=\\n\\n(?:${fieldLabels}):\\n|$)`);
    const match = body.match(pattern);
    return match ? match[1] : null;
  };

  if (config.mode === "list") {
    const items = itemBlocks.length
      ? itemBlocks.map((block, index) => {
          const [headingLine, ...rest] = block.split("\n");
          const body = rest.join("\n");
          const parsedHeading = headingLine.replace(/^###\s*/, "");
          const base = createGuidedEntry(config, parsedHeading.trim() !== "" ? parsedHeading : `${config.itemLabel} ${index + 1}`);
          config.fields.forEach((field) => {
            const parsedValue = extractFieldValue(body, field.label);
            base[field.key] = parsedValue ?? base[field.key];
          });
          return base;
        })
      : [createGuidedEntry(config, `${config.itemLabel} 1`)];

    return { items };
  }

  const items = {};
  config.items.forEach((item) => {
    items[item.key] = createGuidedEntry(config, item.label);
  });

  itemBlocks.forEach((block) => {
    const [headingLine, ...rest] = block.split("\n");
    const heading = headingLine.replace(/^###\s*/, "");
    const itemDef = config.items.find((item) => item.label.toLowerCase() === heading.trim().toLowerCase());
    if (!itemDef) return;

    const body = rest.join("\n");
    const next = { ...items[itemDef.key], label: itemDef.label };
    config.fields.forEach((field) => {
      const parsedValue = extractFieldValue(body, field.label);
      next[field.key] = parsedValue ?? next[field.key];
    });
    items[itemDef.key] = next;
  });

  return { items };
}

function normalizeSections(rawSections) {
  const raw = { ...parseJson(rawSections) };
  const take = (...keys) =>
    keys
      .map((key) => String(raw[key] || "").trim())
      .filter(Boolean)
      .join("\n\n");

  return {
    ...EMPTY_SECTIONS,
    executive_summary: take("executive_summary"),
    positive_signals: take("positive_signals"),
    current_risks: take(
      "current_risks",
      "security_and_access",
      "performance_and_reliability"
    ),
    incident_learnings: take("incident_learnings", "incident_landscape"),
    process_findings_by_area: take(
      "process_findings_by_area",
      "business_context",
      "platform_hosting_assessment",
      "wordpress_core_theme_plugin_health",
      "integrations_and_dependencies"
    ),
    operational_readiness: take(
      "operational_readiness",
      "governance_and_maintenance_readiness"
    ),
    documentation_and_handover: take("documentation_and_handover"),
    recommendations: take(
      "recommendations",
      "prioritized_recommendations_30_60_90"
    ),
    recommendation_matrix: take("recommendation_matrix"),
    consultant_notes: take("consultant_notes", "analyst_comments"),
    section_meta: typeof raw.section_meta === "object" && raw.section_meta ? raw.section_meta : {},
    risk_score: Number(raw.risk_score || 0)
  };
}

function resolveAttachmentUrl(path) {
  if (!path) return "#";
  if (/^https?:\/\//i.test(path)) return path;
  const base = String(client.defaults.baseURL || "").replace(/\/api\/?$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

function sanitizeRatingValue(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return Math.max(0, Math.min(100, numeric));
}

function deriveScoreTone(score) {
  if (!Number.isFinite(score)) return "neutral";
  if (score >= 75) return "high";
  if (score >= 45) return "medium";
  return "low";
}

function deriveStatusTone(status) {
  const value = String(status || "").toLowerCase();
  if (!value) return "neutral";
  if (
    value.includes("critical")
    || value.includes("high risk")
    || value.includes("at risk")
    || value.includes("needs attention")
  ) return "high";
  if (
    value.includes("needs improvement")
    || value.includes("partially")
    || value.includes("priority")
    || value.includes("watch")
    || value.includes("important")
    || value.includes("observed")
    || value.includes("planned")
  ) return "medium";
  return "low";
}

function statusSeverity(status) {
  const tone = deriveStatusTone(status);
  if (tone === "high") return 3;
  if (tone === "medium") return 2;
  if (tone === "low") return 1;
  return 0;
}

function sectionMetaSummary(sectionMeta, sectionKey) {
  const group = sectionMeta?.[sectionKey] || {};
  const entries = Object.values(group);
  const ratings = entries
    .map((item) => Number(item?.rating))
    .filter((value) => Number.isFinite(value) && value >= 0);
  const statuses = entries
    .map((item) => String(item?.status || "").trim())
    .filter(Boolean);

  const rating = ratings.length
    ? Math.round(ratings.reduce((sum, value) => sum + value, 0) / ratings.length)
    : null;

  const status = statuses.length
    ? statuses.sort((left, right) => statusSeverity(right) - statusSeverity(left))[0]
    : "";

  return {
    rating,
    status,
    tone: deriveScoreTone(rating),
    statusTone: deriveStatusTone(status)
  };
}

function sectionCompletionSummary(sectionKey, value, config) {
  const text = String(value || "").trim();
  if (!config) {
    const lengthScore = text.length >= 600 ? 1 : text.length >= 220 ? 0.7 : text.length >= 60 ? 0.45 : text.length > 0 ? 0.2 : 0;
    return {
      ratio: lengthScore,
      label: lengthScore >= 0.8 ? "Strong" : lengthScore >= 0.45 ? "Developing" : lengthScore > 0 ? "Thin" : "Empty",
      tone: lengthScore >= 0.8 ? "low" : lengthScore >= 0.45 ? "medium" : lengthScore > 0 ? "high" : "neutral"
    };
  }

  const parsed = parseGuidedSection(config, text);
  const fields = config.fields.filter((field) => field.key !== "rating" && field.key !== "status");
  const items = config.mode === "list" ? parsed.items : Object.values(parsed.items || {});
  const totalFields = Math.max(1, items.length * fields.length);
  const filledFields = items.reduce((sum, item) => {
    return sum + fields.filter((field) => String(item?.[field.key] || "").trim() !== "").length;
  }, 0);
  const ratio = Math.max(0, Math.min(1, filledFields / totalFields));

  return {
    ratio,
    label: ratio >= 0.8 ? "Strong" : ratio >= 0.45 ? "Developing" : ratio > 0 ? "Thin" : "Empty",
    tone: ratio >= 0.8 ? "low" : ratio >= 0.45 ? "medium" : ratio > 0 ? "high" : "neutral"
  };
}

function sectionCompletionHint(sectionKey, summary) {
  if (!summary || (summary.label !== "Thin" && summary.label !== "Empty")) return "";

  const bySection = {
    executive_summary: "Needs more content: add clear executive subsections with concise client-facing summaries.",
    positive_signals: "Needs more content: add the working practice, why it matters, and the area it protects.",
    current_risks: "Needs more content: describe the risk, its business impact, and the action needed to reduce it.",
    incident_learnings: "Needs more content: note the pattern, the lesson, and the follow-up action.",
    process_findings_by_area: "Needs more content: fill current position, positive signal, process risk, and recommendation for each important area.",
    operational_readiness: "Needs more content: add current state, the readiness gap, and what would strengthen this control.",
    documentation_and_handover: "Needs more content: capture the documentation type, owner, storage location, risk, and recommendation.",
    recommendations: "Needs more content: add the action, expected outcome, and suggested owner so the client can act on it.",
    recommendation_matrix: "Needs more content: add recommendation rows with priority, impact, effort, owner, and expected outcome.",
    consultant_notes: "Needs more content: capture caveats, follow-up questions, and your consultant judgment."
  };

  return bySection[sectionKey] || "Needs more content: add clearer findings, impact, and next-step guidance for this section.";
}

function shortChipLabel(value) {
  const text = String(value || "").trim();
  if (!text) return "Item";
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= 2) return text;
  return `${words.slice(0, 2).join(" ")}...`;
}

function addButtonLabel(sectionKey, itemLabel) {
  if (sectionKey === "current_risks") return "Add Risk";
  if (sectionKey === "positive_signals") return "Add Signal";
  if (sectionKey === "recommendation_matrix") return "Add Action";
  if (sectionKey === "documentation_and_handover") return "Add Doc";
  if (sectionKey === "operational_readiness") return "Add Readiness";
  return `Add ${itemLabel || "Item"}`;
}

function indexToAlphabet(index) {
  let value = Number(index);
  if (!Number.isFinite(value) || value <= 0) return "A";
  let result = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    value = Math.floor((value - 1) / 26);
  }
  return result;
}

function guidedItemCompletionRatio(item, config) {
  const contentFields = (config?.fields || []).filter((field) => field.key !== "rating" && field.key !== "status");
  if (!contentFields.length) return 0;
  const filled = contentFields.filter((field) => String(item?.[field.key] || "").trim() !== "").length;
  return Math.max(0, Math.min(1, filled / contentFields.length));
}

function listItemKeyFromIndex(index) {
  return `idx:${Math.max(0, Number(index) || 0)}`;
}

function parseListItemIndex(itemKey) {
  const match = String(itemKey || "").match(/^idx:(\d+)$/);
  if (!match) return -1;
  return Number(match[1]);
}

export default function ReportsPage({ websiteId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const sectionRefs = useRef({});
  const sectionMenuMiddleButtonRefs = useRef({});
  const sectionMenuNavRef = useRef(null);
  const sectionMenuListRef = useRef(null);
  const sectionMenuCopyStartRefs = useRef({});
  const sectionMenuProgrammaticRef = useRef(false);
  const sectionMenuProgrammaticTimerRef = useRef(null);
  const sectionMenuDragRef = useRef({ active: false, startX: 0, startScrollLeft: 0, moved: false });
  const sectionMenuHoverRef = useRef(false);
  const [workspaceId, setWorkspaceId] = useState(0);
  const [title, setTitle] = useState("WordPress Assessment Draft");
  const [sections, setSections] = useState(EMPTY_SECTIONS);
  const [isAiSeeded, setIsAiSeeded] = useState(false);
  const [evidence, setEvidence] = useState({ questionnaire: [], incidents: [], attachments: [], answer_incidents: [] });
  const [activeTab, setActiveTab] = useState("composer");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [secretCode, setSecretCode] = useState("");
  const [submitResult, setSubmitResult] = useState(null);
  const [latestReport, setLatestReport] = useState(null);
  const [selectedSectionKey, setSelectedSectionKey] = useState(SECTION_DEFS[0].key);
  const [evidenceFilter, setEvidenceFilter] = useState("all");
  const [evidenceSearch, setEvidenceSearch] = useState("");
  const [aiBusyMode, setAiBusyMode] = useState("");
  const [aiDialog, setAiDialog] = useState(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesText, setNotesText] = useState("");
  const [notesBusy, setNotesBusy] = useState(false);
  const [notesError, setNotesError] = useState("");
  const [saveStatus, setSaveStatus] = useState("");
  const [autosaveStatus, setAutosaveStatus] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [guidedFocus, setGuidedFocus] = useState({});
  const [riskMode, setRiskMode] = useState("auto");
  const [lastAiUsed, setLastAiUsed] = useState(null);
  const [draggedGuidedItem, setDraggedGuidedItem] = useState("");
  const [dragTargetGuidedItem, setDragTargetGuidedItem] = useState("");
  const [guidedNavOffsets, setGuidedNavOffsets] = useState({});

  const MAIN_RAIL_LOOP_COPIES = 3;
  const GUIDED_NAV_WINDOW_SIZE = 4;
  const autosaveSkipRef = useRef(true);

  const hasAiUsageInSections = (sectionsValue) => {
    return Number(sectionsValue?.section_meta?._report?.ai_used || 0) === 1;
  };

  const loadWorkspace = async () => {
    if (!websiteId) return;
    setLoading(true);
    try {
      try {
        const settingsResponse = await client.get("/ai/settings");
        setLastAiUsed(settingsResponse.data?.preferences?.last_used || null);
      } catch {
        // ignore settings load failures inside report workspace
      }
      const { data } = await client.get(`/websites/${websiteId}/report-workspace`);
      const workspace = data.workspace || {};
      const serverSections = normalizeSections(workspace.sections_json);

      setWorkspaceId(Number(workspace.id || 0));
      setTitle(workspace.title || "WordPress Assessment Draft");
      const nextSections = serverSections;
      setSections(nextSections);
      setIsAiSeeded(
        Number(workspace.is_ai_seeded || 0) === 1 || hasAiUsageInSections(nextSections)
      );
      setRiskMode(String(nextSections.section_meta?._report?.risk_mode || "auto"));
      setEvidence(data.evidence || { questionnaire: [], incidents: [], attachments: [], answer_incidents: [] });
      setLatestReport(data.latest_report || null);
      if (data.latest_report?.current_secret_code) {
        setSecretCode(data.latest_report.current_secret_code);
      }
      setAutosaveStatus("");
    } catch (error) {
      setSaveStatus(error?.response?.data?.message || "Unable to load report workspace.");
    } finally {
      setHydrated(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkspace();
  }, [websiteId]);

  useEffect(() => {
    autosaveSkipRef.current = true;
  }, [websiteId]);

  useEffect(() => {
    if (!hydrated || !websiteId) return;
    if (autosaveSkipRef.current) {
      autosaveSkipRef.current = false;
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const { data } = await client.post(`/websites/${websiteId}/report-workspace/save`, {
          workspace_id: workspaceId,
          title,
          sections_json: sections,
          is_ai_seeded: (isAiSeeded || hasAiUsageInSections(sections))
        });
        if (data?.workspace_id) {
          setWorkspaceId(Number(data.workspace_id));
        }
        setAutosaveStatus(`Autosaved to database at ${new Date().toLocaleTimeString()}`);
      } catch {
        setAutosaveStatus("Autosave failed. Use Save Draft to store changes.");
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [hydrated, websiteId, workspaceId, title, sections, isAiSeeded]);

  const incidentMap = useMemo(() => {
    const map = new Map();
    (evidence.incidents || []).forEach((incident) => map.set(Number(incident.id), incident));
    return map;
  }, [evidence.incidents]);

  const filteredEvidence = useMemo(() => {
    const search = evidenceSearch.trim().toLowerCase();
    const bySearch = (text) => !search || String(text || "").toLowerCase().includes(search);

    const questionnaireItems = (evidence.questionnaire || []).flatMap((stage) => {
      const answers = parseJson(stage.responses_json);
      return Object.entries(answers).map(([key, value]) => ({
        id: `${stage.stage_key}-${key}`,
        type: "questionnaire",
        title: `${stage.stage_label} / ${key}`,
        subtitle: String(value || "-"),
        payload: { stage, key, value }
      }));
    });

    const attachmentItems = (evidence.attachments || []).map((attachment) => ({
      id: `attachment-${attachment.id}`,
      type: "attachment",
      title: attachment.original_name,
      subtitle: `${attachment.file_kind} | ${attachment.stage_key || "-"} / ${attachment.question_key || "-"}`,
      payload: attachment
    }));

    const incidentItems = (evidence.incidents || []).map((incident) => ({
      id: `incident-${incident.id}`,
      type: "incident",
      title: incident.title,
      subtitle: `${incident.severity} | ${incident.status}`,
      payload: incident
    }));

    const linkItems = (evidence.answer_incidents || []).map((link) => ({
      id: `link-${link.id}`,
      type: "link",
      title: `${link.stage_key} / ${link.question_key}`,
      subtitle: incidentMap.get(Number(link.incident_id))?.title || `Incident #${link.incident_id}`,
      payload: link
    }));

    return [...questionnaireItems, ...attachmentItems, ...incidentItems, ...linkItems].filter((item) => {
      const filterMatches = evidenceFilter === "all" || evidenceFilter === item.type;
      const searchMatches = bySearch(item.title) || bySearch(item.subtitle);
      return filterMatches && searchMatches;
    });
  }, [evidence, evidenceFilter, evidenceSearch, incidentMap]);

  const centerSectionMenuNode = (container, node, behavior = "smooth") => {
    if (!container || !node) return;
    const targetLeft = node.offsetLeft - ((container.clientWidth - node.clientWidth) / 2);
    if (behavior !== "auto") {
      sectionMenuProgrammaticRef.current = true;
      if (sectionMenuProgrammaticTimerRef.current) {
        clearTimeout(sectionMenuProgrammaticTimerRef.current);
      }
      sectionMenuProgrammaticTimerRef.current = setTimeout(() => {
        sectionMenuProgrammaticRef.current = false;
      }, 420);
    }
    container.scrollTo({ left: targetLeft, behavior });
  };

  const centerSectionMenuButton = (sectionKey, behavior = "smooth") => {
    const container = sectionMenuListRef.current;
    const button = sectionMenuMiddleButtonRefs.current[sectionKey];
    if (!container || !button) return;
    centerSectionMenuNode(container, button, behavior);
  };

  const scrollToSection = (sectionKey) => {
    setSelectedSectionKey(sectionKey);
    sectionRefs.current[sectionKey]?.scrollIntoView({ behavior: "smooth", block: "start" });
    centerSectionMenuButton(sectionKey, "smooth");
  };

  const handleHorizontalWheelScroll = (event) => {
    const node = event.currentTarget;
    if (!node || node.scrollWidth <= node.clientWidth) return;
    const primarilyVertical = Math.abs(event.deltaY) >= Math.abs(event.deltaX);
    const delta = primarilyVertical ? event.deltaY : event.deltaX;
    node.scrollLeft += delta;
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    const onWindowWheelCapture = (event) => {
      if (!sectionMenuHoverRef.current) return;
      const node = sectionMenuListRef.current;
      if (!node) return;

      const primarilyVertical = Math.abs(event.deltaY) >= Math.abs(event.deltaX);
      const delta = primarilyVertical ? event.deltaY : event.deltaX;
      node.scrollLeft += delta;
      event.preventDefault();
      event.stopPropagation();
    };

    window.addEventListener("wheel", onWindowWheelCapture, { passive: false, capture: true });
    return () => window.removeEventListener("wheel", onWindowWheelCapture, true);
  }, [MAIN_RAIL_LOOP_COPIES]);

  useEffect(() => {
    const node = sectionMenuListRef.current;
    if (!node) return undefined;

    const getOneSetWidth = () => {
      const firstCopy = sectionMenuCopyStartRefs.current[0];
      const secondCopy = sectionMenuCopyStartRefs.current[1];
      if (firstCopy && secondCopy) {
        return secondCopy.offsetLeft - firstCopy.offsetLeft;
      }
      return node.scrollWidth / MAIN_RAIL_LOOP_COPIES;
    };

    const ensureMiddleBand = () => {
      if (sectionMenuProgrammaticRef.current) return;
      const oneSetWidth = getOneSetWidth();
      if (!oneSetWidth) return;
      // Keep a wide safe band so selecting edge items (first/last) does not trigger jumpy wrap.
      const viewportBuffer = node.clientWidth;
      const lower = oneSetWidth - viewportBuffer;
      const upper = (oneSetWidth * 2) + viewportBuffer;
      if (node.scrollLeft < lower) {
        node.scrollLeft += oneSetWidth;
      } else if (node.scrollLeft > upper) {
        node.scrollLeft -= oneSetWidth;
      }
    };

    // Start in middle copy so left/right continuity is available immediately.
    const init = () => {
      const oneSetWidth = getOneSetWidth();
      if (!oneSetWidth) return;
      if (node.scrollLeft < oneSetWidth * 0.5 || node.scrollLeft > oneSetWidth * 1.5) {
        node.scrollLeft = oneSetWidth;
      }
    };

    init();
    node.addEventListener("scroll", ensureMiddleBand, { passive: true });
    window.addEventListener("resize", init);

    return () => {
      node.removeEventListener("scroll", ensureMiddleBand);
      window.removeEventListener("resize", init);
      if (sectionMenuProgrammaticTimerRef.current) {
        clearTimeout(sectionMenuProgrammaticTimerRef.current);
      }
    };
  }, [MAIN_RAIL_LOOP_COPIES]);

  useEffect(() => {
    centerSectionMenuButton(selectedSectionKey, "smooth");
  }, [selectedSectionKey]);

  useEffect(() => {
    const onResize = () => centerSectionMenuButton(selectedSectionKey, "auto");
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [selectedSectionKey]);

  const loopedMainSections = useMemo(() => {
    return Array.from({ length: MAIN_RAIL_LOOP_COPIES }, (_, copyIndex) =>
      SECTION_DEFS.map((section, index) => ({
        ...section,
        copyIndex,
        displayIndex: index + 1
      }))
    ).flat();
  }, [MAIN_RAIL_LOOP_COPIES]);

  const setSectionText = (sectionKey, nextText) => {
    setSections((prev) => ({ ...prev, [sectionKey]: nextText }));
  };

  const setSectionMeta = (sectionKey, itemKey, nextMeta) => {
    setSections((prev) => ({
      ...prev,
      section_meta: {
        ...(prev.section_meta || {}),
        [sectionKey]: {
          ...((prev.section_meta || {})[sectionKey] || {}),
          [itemKey]: {
            ...(((prev.section_meta || {})[sectionKey] || {})[itemKey] || {}),
            ...nextMeta
          }
        }
      }
    }));
  };

  const syncRecommendationMatrixMeta = (sectionKey, items) => {
    if (sectionKey !== "recommendation_matrix") return;
    const normalized = (items || []).map((item) => ({
      label: item.label || "",
      recommendation: item.recommendation || "",
      area: item.area || "",
      priority: item.priority || "",
      impact: item.impact || "",
      effort: item.effort || "",
      suggested_owner: item.suggested_owner || "",
      reason: item.reason || ""
    }));
    setSections((prev) => ({
      ...prev,
      section_meta: {
        ...(prev.section_meta || {}),
        [sectionKey]: {
          ...((prev.section_meta || {})[sectionKey] || {}),
          _rows: normalized
        }
      }
    }));
  };

  const getGuidedSectionState = (sectionKey) => {
    const config = GUIDED_SECTION_CONFIGS[sectionKey];
    if (!config) return null;
    return parseGuidedSection(config, sections[sectionKey] || "");
  };

  const updateGuidedField = (sectionKey, itemKey, fieldKey, value) => {
    const config = GUIDED_SECTION_CONFIGS[sectionKey];
    if (!config) return;

    const guidedState = parseGuidedSection(config, sections[sectionKey] || "");
    if (config.mode === "list") {
      const parsedIndex = parseListItemIndex(itemKey);
      const targetIndex = parsedIndex >= 0 ? parsedIndex : 0;
      const nextItems = guidedState.items.map((item, index) =>
        (index === targetIndex) ? { ...item, [fieldKey]: value } : item
      );
      setSectionText(sectionKey, serializeGuidedSection(config, { items: nextItems }));
      syncRecommendationMatrixMeta(sectionKey, nextItems);
      if (fieldKey === "rating" || fieldKey === "status") {
        setSectionMeta(sectionKey, itemKey, {
          ...(fieldKey === "rating" ? { rating: sanitizeRatingValue(value) } : {}),
          ...(fieldKey === "status" ? { status: value } : {})
        });
      }
      return;
    }

    const nextItems = {
      ...guidedState.items,
      [itemKey]: {
        ...guidedState.items[itemKey],
        [fieldKey]: value
      }
    };
    setSectionText(sectionKey, serializeGuidedSection(config, { items: nextItems }));
    syncRecommendationMatrixMeta(sectionKey, Object.values(nextItems));
    if (fieldKey === "rating" || fieldKey === "status") {
      setSectionMeta(sectionKey, itemKey, {
        ...(fieldKey === "rating" ? { rating: sanitizeRatingValue(value) } : {}),
        ...(fieldKey === "status" ? { status: value } : {})
      });
    }
  };

  const addGuidedListItem = (sectionKey) => {
    const config = GUIDED_SECTION_CONFIGS[sectionKey];
    if (!config || config.mode !== "list") return;
    const guidedState = parseGuidedSection(config, sections[sectionKey] || "");
    const nextLabel = `${config.itemLabel} ${guidedState.items.length + 1}`;
    const nextItems = [...guidedState.items, createGuidedEntry(config, nextLabel)];
    setGuidedFocus((prev) => ({ ...prev, [sectionKey]: listItemKeyFromIndex(nextItems.length - 1) }));
    setSectionText(sectionKey, serializeGuidedSection(config, { items: nextItems }));
    syncRecommendationMatrixMeta(sectionKey, nextItems);
  };

  const removeGuidedListItem = (sectionKey, itemKey) => {
    const config = GUIDED_SECTION_CONFIGS[sectionKey];
    if (!config || config.mode !== "list") return;
    const guidedState = parseGuidedSection(config, sections[sectionKey] || "");
    const targetIndex = parseListItemIndex(itemKey);
    const nextItems = guidedState.items.filter((_, index) => index !== targetIndex);
    setSectionText(sectionKey, serializeGuidedSection(config, { items: nextItems.length ? nextItems : [createGuidedEntry(config, `${config.itemLabel} 1`)] }));
    const nextFocusIndex = Math.max(0, Math.min(targetIndex, (nextItems.length ? nextItems.length : 1) - 1));
    setGuidedFocus((prev) => ({ ...prev, [sectionKey]: listItemKeyFromIndex(nextFocusIndex) }));
    syncRecommendationMatrixMeta(sectionKey, nextItems.length ? nextItems : [createGuidedEntry(config, `${config.itemLabel} 1`)]);
    setSections((prev) => {
      const nextSectionMeta = { ...((prev.section_meta || {})[sectionKey] || {}) };
      delete nextSectionMeta[itemKey];
      return {
        ...prev,
        section_meta: {
          ...(prev.section_meta || {}),
          [sectionKey]: nextSectionMeta
        }
      };
    });
  };

  const moveGuidedListItem = (sectionKey, itemKey, direction) => {
    const config = GUIDED_SECTION_CONFIGS[sectionKey];
    if (!config || config.mode !== "list") return;
    const guidedState = parseGuidedSection(config, sections[sectionKey] || "");
    const currentIndex = parseListItemIndex(itemKey);
    if (currentIndex < 0) return;
    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= guidedState.items.length) return;

    const nextItems = [...guidedState.items];
    const [currentItem] = nextItems.splice(currentIndex, 1);
    nextItems.splice(targetIndex, 0, currentItem);
    setGuidedFocus((prev) => ({ ...prev, [sectionKey]: listItemKeyFromIndex(targetIndex) }));
    setSectionText(sectionKey, serializeGuidedSection(config, { items: nextItems }));
    syncRecommendationMatrixMeta(sectionKey, nextItems);
  };

  const reorderGuidedListItem = (sectionKey, sourceItemKey, targetItemKey) => {
    const config = GUIDED_SECTION_CONFIGS[sectionKey];
    if (!config || config.mode !== "list" || !sourceItemKey || !targetItemKey || sourceItemKey === targetItemKey) return;
    const guidedState = parseGuidedSection(config, sections[sectionKey] || "");
    const sourceIndex = parseListItemIndex(sourceItemKey);
    const targetIndex = parseListItemIndex(targetItemKey);
    if (sourceIndex < 0 || targetIndex < 0) return;

    const nextItems = [...guidedState.items];
    const [sourceItem] = nextItems.splice(sourceIndex, 1);
    nextItems.splice(targetIndex, 0, sourceItem);
    setGuidedFocus((prev) => ({ ...prev, [sectionKey]: listItemKeyFromIndex(targetIndex) }));
    setSectionText(sectionKey, serializeGuidedSection(config, { items: nextItems }));
    syncRecommendationMatrixMeta(sectionKey, nextItems);
  };

  const suggestSectionKey = (item) => {
    const stageKey = String(item?.payload?.stage_key || item?.payload?.stage?.stage_key || "").toLowerCase();
    const stageLabel = String(item?.payload?.stage?.stage_label || "").toLowerCase();
    const combined = `${stageKey} ${stageLabel}`;

    if (item.type === "incident" || item.type === "link") return "incident_learnings";
    if (combined.includes("business")) return "process_findings_by_area";
    if (combined.includes("host") || combined.includes("dns") || combined.includes("infrastructure")) return "process_findings_by_area";
    if (combined.includes("plugin") || combined.includes("theme") || combined.includes("wordpress") || combined.includes("core")) return "process_findings_by_area";
    if (combined.includes("security") || combined.includes("access") || combined.includes("admin")) return "current_risks";
    if (combined.includes("performance") || combined.includes("speed") || combined.includes("reliability")) return "current_risks";
    if (combined.includes("integration") || combined.includes("dependency") || combined.includes("third")) return "process_findings_by_area";
    if (combined.includes("governance") || combined.includes("maintenance") || combined.includes("support")) return "operational_readiness";
    return item.type === "attachment" ? "consultant_notes" : "executive_summary";
  };

  const buildEvidenceText = (item) => {
    if (item.type === "incident") {
      return [
        item.title,
        `Severity / Status: ${item.payload.severity || "-"} / ${item.payload.status || "-"}`,
        `Issue: ${item.payload.issue_summary || item.payload.details || "-"}`,
        `Root Cause: ${item.payload.root_cause || "-"}`,
        `Business Impact: ${item.payload.business_impact || "-"}`
      ].join("\n");
    }

    if (item.type === "attachment") {
      return [
        `Attachment: ${item.payload.original_name}`,
        `Type: ${item.payload.file_kind || "-"}`,
        `Source: ${item.payload.stage_key || "-"} / ${item.payload.question_key || "-"}`,
        `Reference: ${resolveAttachmentUrl(item.payload.public_url)}`
      ].join("\n");
    }

    if (item.type === "link") {
      return [
        `Incident linked to answer: ${item.title}`,
        `Linked incident: ${item.subtitle}`,
        item.payload.note ? `Reviewer note: ${item.payload.note}` : null
      ].filter(Boolean).join("\n");
    }

    return [item.title, item.subtitle].join("\n");
  };

  const appendToSection = (sectionKey, text) => {
    if (!text) return;
    setSections((prev) => {
      const current = String(prev[sectionKey] || "").trim();
      const nextText = current ? `${current}\n\n${text}` : text;
      return { ...prev, [sectionKey]: nextText };
    });
    setActiveTab("composer");
    setTimeout(() => scrollToSection(sectionKey), 0);
  };

  const renderEvidenceActions = (item) => {
    const suggestedSectionKey = suggestSectionKey(item);
    const suggestedSection = SECTION_DEFS.find((section) => section.key === suggestedSectionKey);

    return (
      <div className="evidence-card-actions">
        <p className="muted compact-status">
          Suggested section: <strong>{suggestedSection?.label || "Executive Summary"}</strong>
        </p>
        <button
          type="button"
          className="button-link ghost compact-action-btn"
          onClick={() => appendToSection(suggestedSectionKey, buildEvidenceText(item))}
        >
          Add To Report
        </button>
        <button
          type="button"
          className="button-link ghost compact-action-btn"
          onClick={() => {
            setActiveTab("composer");
            setSelectedSectionKey(suggestedSectionKey);
            setTimeout(() => scrollToSection(suggestedSectionKey), 0);
          }}
        >
          Open Section
        </button>
        {item.type === "attachment" ? (
          <a className="button-link ghost compact-action-btn" href={resolveAttachmentUrl(item.payload.public_url)} target="_blank" rel="noreferrer">
            Open File
          </a>
        ) : null}
      </div>
    );
  };

  const generateAiStarter = async () => {
    if (!websiteId) return;
    setGenerating(true);
    try {
      const { data } = await client.post(`/websites/${websiteId}/report-workspace/seed-ai`);
      setWorkspaceId(Number(data.workspace_id || 0));
      setSections(normalizeSections(data.sections_json));
      setIsAiSeeded(true);
      await loadWorkspace();
    } finally {
      setGenerating(false);
    }
  };

  const saveDraft = async () => {
    if (!websiteId) return;
    setSaving(true);
    setSaveStatus("");
    try {
      const { data } = await client.post(`/websites/${websiteId}/report-workspace/save`, {
        workspace_id: workspaceId,
        title,
        sections_json: sections,
        is_ai_seeded: (isAiSeeded || hasAiUsageInSections(sections))
      });
      setWorkspaceId(Number(data.workspace_id || workspaceId));
      await loadWorkspace();
      setSaveStatus(`Draft saved at ${new Date().toLocaleTimeString()}`);
    } catch (error) {
      setSaveStatus(error?.response?.data?.message || "Draft save failed. The report was not stored.");
    } finally {
      setSaving(false);
    }
  };

  const submitDraft = async () => {
    if (!websiteId || !workspaceId) return;
    setSubmitting(true);
    setSaveStatus("");
    try {
      const { data } = await client.post(`/websites/${websiteId}/report-workspace/submit`, {
        workspace_id: workspaceId,
        title,
        sections_json: sections,
        is_ai_seeded: (isAiSeeded || hasAiUsageInSections(sections)),
        secret_code: secretCode || undefined
      });
      setSubmitResult(data);
      setLatestReport({
        id: data.report_id,
        public_slug: String(data.public_frontend_url || "").split("/").pop(),
        public_frontend_url: data.public_frontend_url,
        current_secret_code: data.secret_code || ""
      });
      if (data.secret_code) {
        setSecretCode(data.secret_code);
      }
      await loadWorkspace();
      setSaveStatus(`${data.is_update ? "Report updated" : "Report generated"} at ${new Date().toLocaleTimeString()}`);
    } catch (error) {
      setSaveStatus(error?.response?.data?.message || "Report submission failed. Changes may not have been stored.");
    } finally {
      setSubmitting(false);
    }
  };

  const runAiAssist = async (mode, override = {}, aiRules = null) => {
    if (!websiteId) return;
    const busyKey = override.busyKey || mode;
    setAiBusyMode(busyKey);
    setSaveStatus("");
    try {
      const { data } = await client.post("/ai/text-assist", {
        website_id: websiteId,
        mode,
        field_label: override.fieldLabel || selectedSection.label,
        current_text: override.currentText ?? (sections[selectedSection.key] || ""),
        context: {
          page: "reports",
          report_title: title,
          section_key: selectedSection.key,
          section_label: selectedSection.label,
          section_hint: selectedSection.hint,
          current_section_text: sections[selectedSection.key] || "",
          subsection_label: override.subsectionLabel || "",
          field_key: override.fieldKey || "",
          field_label: override.fieldLabel || selectedSection.label,
          ai_rules: aiRules
        }
      }, {
        skipAuthRedirect: true
      });
      if (override.onResult) {
        override.onResult(data?.data?.text || "");
      } else {
        setSections((prev) => ({ ...prev, [selectedSection.key]: data?.data?.text || "" }));
      }
      setSections((prev) => ({
        ...prev,
        section_meta: {
          ...(prev.section_meta || {}),
          _report: {
            ...((prev.section_meta || {})._report || {}),
            ai_used: 1
          }
        }
      }));
      setIsAiSeeded(true);
      if (data?.data?.provider) {
        setLastAiUsed({
          provider: data.data.provider,
          model: data.data.model,
          used_at: new Date().toISOString()
        });
      }
      if (data?.data?.fallback && data?.message) {
        setSaveStatus(data.message);
      }
    } catch (error) {
      setSaveStatus(error?.response?.data?.message || "AI assist could not generate content for this field.");
    } finally {
      setAiBusyMode("");
    }
  };

  const openAiDialog = (mode, override = {}) => {
    setAiDialog({ mode, override });
  };

  const ensureAiReady = async () => {
    try {
      const { data } = await client.get("/ai/settings");
      const activeProvider = String(data?.preferences?.active_provider || "gemini");
      const provider = (data?.providers || []).find((item) => item.provider === activeProvider);
      const selectedModel = String(provider?.selected_model || "");
      const { data: testData } = await client.post("/ai/settings/test", {
        provider: activeProvider,
        model: selectedModel || undefined
      });
      if (testData?.ok) {
        return { ok: true };
      }
      return {
        ok: false,
        message: testData?.message || `${activeProvider} (${selectedModel || "default model"}) is not ready.`
      };
    } catch (error) {
      return {
        ok: false,
        message: error?.response?.data?.message || "AI provider is not configured. Open Settings and verify active provider model and API key."
      };
    }
  };

  const ingestNotes = async () => {
    if (!websiteId) return;
    if (!notesText.trim()) {
      setNotesError("Paste meeting notes before running the import.");
      return;
    }
    setNotesError("");
    const readiness = await ensureAiReady();
    if (!readiness.ok) {
      setNotesError(readiness.message);
      return;
    }
    setNotesBusy(true);
    try {
      const { data } = await client.post(`/websites/${websiteId}/notes/ingest`, {
        notes_text: notesText
      });
      setNotesDialogOpen(false);
      setNotesText("");
      setSaveStatus(data?.message || "Notes ingested and stored.");
      await loadWorkspace();
    } catch (error) {
      setNotesError(error?.response?.data?.message || "Unable to ingest notes. Verify AI settings and try again.");
    } finally {
      setNotesBusy(false);
    }
  };

  const applyAiDialog = async (options) => {
    const request = aiDialog;
    setAiDialog(null);
    if (!request) return;

    const rules = options?.applyRules
      ? {
          enabled: true,
          min_characters: Number(options.minCharacters || 0),
          min_headings: Number(options.minHeadings || 0),
          require_bullet_points: Boolean(options.requireBullets),
          extra_instruction: String(options.extraInstruction || "").trim()
        }
      : { enabled: false };

    const readiness = await ensureAiReady();
    if (!readiness.ok) {
      setSaveStatus(readiness.message);
      return;
    }

    await runAiAssist(request.mode, request.override || {}, rules);
  };

  if (!websiteId) {
    return <main className="page-grid"><section className="card-modern"><p>Select a website from the top switcher to prepare reports.</p></section></main>;
  }

  const selectedSection = SECTION_DEFS.find((section) => section.key === selectedSectionKey) || SECTION_DEFS[0];
  const sectionSummaries = useMemo(() => SECTION_DEFS.reduce((acc, section) => {
    acc[section.key] = sectionMetaSummary(sections.section_meta, section.key);
    return acc;
  }, {}), [sections.section_meta]);
  const sectionCompletionMap = useMemo(() => SECTION_DEFS.reduce((acc, section) => {
    acc[section.key] = sectionCompletionSummary(section.key, sections[section.key], GUIDED_SECTION_CONFIGS[section.key] || null);
    return acc;
  }, {}), [sections]);
  const suggestedRiskScore = useMemo(() => {
    const values = Object.values(sectionSummaries)
      .map((item) => item.rating)
      .filter((value) => Number.isFinite(value));
    if (values.length) {
      return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
    }
    const completionValues = Object.values(sectionCompletionMap)
      .map((item) => Number(item?.ratio || 0))
      .filter((value) => Number.isFinite(value));
    if (!completionValues.length) return null;
    return Math.round((completionValues.reduce((sum, value) => sum + value, 0) / completionValues.length) * 100);
  }, [sectionSummaries, sectionCompletionMap]);
  const selectedGuidedConfig = GUIDED_SECTION_CONFIGS[selectedSection.key] || null;
  const selectedGuidedState = selectedGuidedConfig ? getGuidedSectionState(selectedSection.key) : null;
  const guidedNavItems = selectedGuidedConfig
    ? (selectedGuidedConfig.mode === "list"
      ? selectedGuidedState.items.map((item, index) => ({ key: listItemKeyFromIndex(index), label: item.label || `${selectedGuidedConfig.itemLabel} ${index + 1}` }))
      : selectedGuidedConfig.items.map((item) => ({ key: item.key, label: item.label })))
    : [];
  const activeGuidedKey = guidedFocus[selectedSection.key]
    || guidedNavItems[0]?.key
    || "";
  const totalGuidedNavItems = guidedNavItems.length;
  const maxGuidedNavOffset = Math.max(0, totalGuidedNavItems - GUIDED_NAV_WINDOW_SIZE);
  const guidedNavOffset = Math.max(
    0,
    Math.min(guidedNavOffsets[selectedSection.key] || 0, maxGuidedNavOffset)
  );
  const visibleGuidedNavItems = guidedNavItems.slice(guidedNavOffset, guidedNavOffset + GUIDED_NAV_WINDOW_SIZE);
  const canShiftGuidedNav = totalGuidedNavItems > GUIDED_NAV_WINDOW_SIZE;
  const canShiftGuidedNavPrev = canShiftGuidedNav && guidedNavOffset > 0;
  const canShiftGuidedNavNext = canShiftGuidedNav && guidedNavOffset < maxGuidedNavOffset;
  const activeGuidedItem = selectedGuidedConfig
    ? (selectedGuidedConfig.mode === "list"
      ? selectedGuidedState.items[parseListItemIndex(activeGuidedKey)] || selectedGuidedState.items[0]
      : selectedGuidedState.items[activeGuidedKey])
    : null;
  const activeGuidedMeta = activeGuidedItem
    ? (((sections.section_meta || {})[selectedSection.key] || {})[selectedGuidedConfig?.mode === "list" ? activeGuidedKey : activeGuidedKey] || {})
    : {};
  const publicFrontendUrl = submitResult?.public_frontend_url || latestReport?.public_frontend_url || "";
  const isReportGenerated = Boolean(latestReport?.id || submitResult?.report_id);
  const displaySecretCode = submitResult?.secret_code || latestReport?.current_secret_code || secretCode || "";
  const outlineCompletion = useMemo(() => {
    const values = Object.values(sectionCompletionMap).map((item) => Number(item?.ratio || 0));
    if (!values.length) return 0;
    return Math.round((values.reduce((sum, value) => sum + value, 0) / values.length) * 100);
  }, [sectionCompletionMap]);
  const reportModeLabel = (isAiSeeded || hasAiUsageInSections(sections)) ? "Hybrid" : "Manual";

  const shiftGuidedNavWindow = (direction) => {
    if (!guidedNavItems.length) return;

    const moveStep = direction >= 0 ? 1 : -1;
    const activeIndex = guidedNavItems.findIndex((item) => item.key === activeGuidedKey);
    if (activeIndex >= 0) {
      const nextIndex = Math.max(0, Math.min(activeIndex + moveStep, guidedNavItems.length - 1));
      if (nextIndex !== activeIndex) {
        setGuidedFocus((prev) => ({ ...prev, [selectedSection.key]: guidedNavItems[nextIndex].key }));
      }
    }

    if (!canShiftGuidedNav) return;
    setGuidedNavOffsets((prev) => {
      const current = Math.max(0, Math.min(prev[selectedSection.key] || 0, maxGuidedNavOffset));
      const next = Math.max(0, Math.min(current + moveStep, maxGuidedNavOffset));
      if (next === current) return prev;
      return { ...prev, [selectedSection.key]: next };
    });
  };

  useEffect(() => {
    if (!hydrated || riskMode !== "auto" || !Number.isFinite(suggestedRiskScore)) return;
    setSections((prev) => {
      if (Number(prev.risk_score || 0) === Number(suggestedRiskScore || 0)) return prev;
      return {
        ...prev,
        risk_score: suggestedRiskScore,
        section_meta: {
          ...(prev.section_meta || {}),
          _report: {
            ...((prev.section_meta || {})._report || {}),
            risk_mode: "auto"
          }
        }
      };
    });
  }, [hydrated, riskMode, suggestedRiskScore]);

  useEffect(() => {
    if (!guidedNavItems.length) return;
    const activeIndex = guidedNavItems.findIndex((item) => item.key === activeGuidedKey);
    if (activeIndex < 0) return;

    setGuidedNavOffsets((prev) => {
      const current = Math.max(0, Math.min(prev[selectedSection.key] || 0, maxGuidedNavOffset));
      let next = current;

      if (activeIndex < current) {
        next = activeIndex;
      } else if (activeIndex >= current + GUIDED_NAV_WINDOW_SIZE) {
        next = activeIndex - GUIDED_NAV_WINDOW_SIZE + 1;
      }

      next = Math.max(0, Math.min(next, maxGuidedNavOffset));
      if (next === current) return prev;
      return { ...prev, [selectedSection.key]: next };
    });
  }, [activeGuidedKey, guidedNavItems, selectedSection.key, maxGuidedNavOffset, GUIDED_NAV_WINDOW_SIZE]);

  useEffect(() => {
    const onGenerate = () => {
      if (!generating) {
        generateAiStarter();
      }
    };
    const onSave = () => {
      if (!saving) {
        saveDraft();
      }
    };

    window.addEventListener("wpa:reports-generate", onGenerate);
    window.addEventListener("wpa:reports-save", onSave);
    const onTabChangeRequest = (event) => {
      const next = event?.detail?.tab;
      if (next === "composer" || next === "evidence") {
        setActiveTab(next);
      }
    };
    window.addEventListener("wpa:reports-tab-change-request", onTabChangeRequest);
    return () => {
      window.removeEventListener("wpa:reports-generate", onGenerate);
      window.removeEventListener("wpa:reports-save", onSave);
      window.removeEventListener("wpa:reports-tab-change-request", onTabChangeRequest);
    };
  }, [generating, saving, websiteId, workspaceId, title, sections, isAiSeeded]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("wpa:reports-active-tab", { detail: { tab: activeTab } }));
  }, [activeTab]);

  useEffect(() => {
    const pending = location.state?.pendingWorkspaceAction || location.state?.pendingReportAction;
    const targetTab = location.state?.targetTab;
    if (targetTab === "composer" || targetTab === "evidence") {
      setActiveTab(targetTab);
    }

    if (pending === "wpa:reports-generate" && !generating) {
      generateAiStarter();
    }
    if (pending === "wpa:reports-save" && !saving) {
      saveDraft();
    }

    if (pending || targetTab) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, generating, saving]);

  const copyShareAccess = async () => {
    if (!publicFrontendUrl) return;
    await navigator.clipboard.writeText(`Report URL: ${publicFrontendUrl}\nSecret Code: ${displaySecretCode}`);
  };

  const shareAccess = async () => {
    if (!publicFrontendUrl) return;
    if (!navigator.share) {
      await copyShareAccess();
      return;
    }
    try {
      await navigator.share({
        title: title || "WordPress Assessment Report",
        text: `Secret Code: ${displaySecretCode}`,
        url: publicFrontendUrl
      });
    } catch {
      // no-op when share is cancelled
    }
  };

  const shareByEmail = () => {
    if (!publicFrontendUrl) return;
    const subject = encodeURIComponent(title || "WordPress Assessment Report");
    const body = encodeURIComponent(`Report URL: ${publicFrontendUrl}\nSecret Code: ${displaySecretCode}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <main className="page-grid reports-workspace-grid">
      {lastAiUsed?.provider ? (
        <p className="composer-ai-indicator reports-inline-ai">
          Last AI used: <strong>{String(lastAiUsed.provider).toUpperCase()}</strong>
          {lastAiUsed.model ? <span>{lastAiUsed.model}</span> : null}
        </p>
      ) : null}

      {activeTab === "composer" ? (
        <section className="reports-composer-shell">
          <aside className="card-modern report-outline-card">
            <div className="report-outline-head">
              <div className="report-outline-progress-rail">
                <div className={`report-outline-progress-fill report-outline-progress-fill-${outlineCompletion >= 80 ? "low" : outlineCompletion >= 45 ? "medium" : "high"}`} style={{ width: `${outlineCompletion}%` }} />
              </div>
              <div className="section-head">
                <h4>Report Outline</h4>
                <span className={`status-chip ${(reportModeLabel === "Hybrid") ? "status-monitoring" : "status-open"}`}>{reportModeLabel}</span>
              </div>
            </div>
            <div className="report-outline-controls">
              <label>
                <span>Report Title</span>
                <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="WordPress Process Assessment Report" />
              </label>
              <label>
                <span>Risk Score (0-100)</span>
                <div className="risk-score-combo">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={sections.risk_score || 0}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value || 0);
                      setRiskMode("manual");
                      setSections((prev) => ({
                        ...prev,
                        risk_score: nextValue,
                        section_meta: {
                          ...(prev.section_meta || {}),
                          _report: {
                            ...((prev.section_meta || {})._report || {}),
                            risk_mode: "manual"
                          }
                        }
                      }));
                    }}
                  />
                  <button
                    type="button"
                    className="risk-live-btn"
                    onClick={() => {
                      setRiskMode("auto");
                      setSections((prev) => ({
                        ...prev,
                        risk_score: suggestedRiskScore ?? prev.risk_score,
                        section_meta: {
                          ...(prev.section_meta || {}),
                          _report: {
                            ...((prev.section_meta || {})._report || {}),
                            risk_mode: "auto"
                          }
                        }
                      }));
                    }}
                    disabled={!Number.isFinite(suggestedRiskScore)}
                  >
                    Auto{Number.isFinite(suggestedRiskScore) ? `(${suggestedRiskScore})` : ""}
                  </button>
                </div>
              </label>
              <div className="report-outline-note-import">
                <span>Meeting Notes</span>
                <button type="button" className="button-link" onClick={() => setNotesDialogOpen(true)}>
                  Import Notes
                </button>
                <small className="muted">Auto-update questionnaire, incidents, and report sections.</small>
              </div>
            </div>
          </aside>

          <section className="reports-composer">
            <nav
              ref={sectionMenuNavRef}
              className="report-section-menu"
              aria-label="Report sections"
              onMouseEnter={() => { sectionMenuHoverRef.current = true; }}
              onMouseLeave={() => { sectionMenuHoverRef.current = false; }}
            >
              <div
                ref={sectionMenuListRef}
                className="report-section-menu-list"
                onWheel={handleHorizontalWheelScroll}
                onMouseDown={(event) => {
                  if (event.button !== 0) return;
                  event.preventDefault();
                  const node = event.currentTarget;
                  sectionMenuDragRef.current = {
                    active: true,
                    startX: event.clientX,
                    startScrollLeft: node.scrollLeft,
                    moved: false
                  };
                  node.classList.add("dragging");

                  const onMove = (moveEvent) => {
                    if (!sectionMenuDragRef.current.active) return;
                    const delta = moveEvent.clientX - sectionMenuDragRef.current.startX;
                    if (Math.abs(delta) > 3) {
                      sectionMenuDragRef.current.moved = true;
                    }
                    node.scrollLeft = sectionMenuDragRef.current.startScrollLeft - delta;
                    moveEvent.preventDefault();
                  };

                  const onUp = () => {
                    sectionMenuDragRef.current.active = false;
                    node.classList.remove("dragging");
                    centerSectionMenuButton(selectedSectionKey, "smooth");
                    window.removeEventListener("mousemove", onMove);
                  };

                  window.addEventListener("mousemove", onMove);
                  window.addEventListener("mouseup", onUp, { once: true });
                }}
              >
              {loopedMainSections.map((section) => (
                <button
                  key={`${section.copyIndex}-${section.key}`}
                  type="button"
                  ref={(node) => {
                    if (section.copyIndex === 1) {
                      sectionMenuMiddleButtonRefs.current[section.key] = node;
                    }
                    if (section.displayIndex === 1) {
                      sectionMenuCopyStartRefs.current[section.copyIndex] = node;
                    }
                  }}
                  className={`report-outline-item ${selectedSectionKey === section.key ? "active" : ""}`}
                  onClick={(event) => {
                    if (sectionMenuDragRef.current.moved) {
                      event.preventDefault();
                      sectionMenuDragRef.current.moved = false;
                      return;
                    }
                    scrollToSection(section.key);
                  }}
                >
                  <div className="report-outline-heading">
                    <span className="report-outline-index">{indexToAlphabet(section.displayIndex)}</span>
                    <strong>{section.label}</strong>
                  </div>
                  <span>{Math.round((sectionCompletionMap[section.key]?.ratio || 0) * 100)}% complete</span>
                </button>
              ))}
              </div>
            </nav>
            <section className={`report-section-editor report-section-editor-focused ${selectedSection.key === "executive_summary" ? "report-section-editor-executive" : ""} ${selectedGuidedConfig ? "report-section-editor-guided" : ""}`} ref={(node) => { sectionRefs.current[selectedSection.key] = node; }}>
              <div className={`section-head ${selectedSection.key === "executive_summary" ? "section-head-executive" : ""}`}>
                <div>
                  <h4>{selectedSection.label}</h4>
                </div>
                <span className={`status-chip ${String(sections[selectedSection.key] || "").trim() ? "status-active" : "status-open"}`}>
                  {String(sections[selectedSection.key] || "").trim() ? "Drafted" : "Pending"}
                </span>
              </div>
              {selectedGuidedConfig ? (
                <section className="guided-section-builder">
                  <div className="guided-section-actions">
                    {selectedGuidedConfig.mode === "list" ? (
                      <button
                        type="button"
                        className="guided-add-btn"
                        onClick={() => addGuidedListItem(selectedSection.key)}
                        title={`Add ${selectedGuidedConfig.itemLabel}`}
                      >
                        <span>+</span>
                        {addButtonLabel(selectedSection.key, selectedGuidedConfig.itemLabel)}
                      </button>
                    ) : null}
                  </div>

                  <div className="guided-nav-rail">
                      <button
                        type="button"
                        className="guided-nav-arrow guided-nav-arrow-rail"
                        onClick={() => shiftGuidedNavWindow(-1)}
                        disabled={!canShiftGuidedNavPrev}
                        aria-label="Previous sub section items"
                      >
                        {"<"}
                      </button>
                      <div className="guided-chip-row guided-chip-row-shell">
                        {visibleGuidedNavItems.map((item, index) => {
                        const sourceItem = selectedGuidedConfig.mode === "list"
                          ? selectedGuidedState.items[parseListItemIndex(item.key)]
                          : selectedGuidedState.items?.[item.key];
                        const completion = Math.round(guidedItemCompletionRatio(sourceItem, selectedGuidedConfig) * 100);
                        const itemTone = completion >= 80 ? "complete" : completion > 0 ? "partial" : "empty";
                        const itemIndex = guidedNavOffset + index + 1;
                        return (
                        <button
                          key={item.key}
                          type="button"
                          className={`guided-chip guided-chip-card guided-chip-card-${itemTone} ${activeGuidedKey === item.key ? "active" : ""} ${dragTargetGuidedItem === item.key ? "drag-target" : ""} ${draggedGuidedItem === item.key ? "drag-source" : ""}`}
                          onClick={() => setGuidedFocus((prev) => ({ ...prev, [selectedSection.key]: item.key }))}
                          draggable={selectedSection.key === "recommendation_matrix"}
                          title={item.label}
                          onDragStart={() => {
                            setDraggedGuidedItem(item.key);
                            setDragTargetGuidedItem("");
                          }}
                          onDragEnter={() => {
                            if (selectedSection.key === "recommendation_matrix" && draggedGuidedItem && draggedGuidedItem !== item.key) {
                              setDragTargetGuidedItem(item.key);
                            }
                          }}
                          onDragOver={(event) => {
                            if (selectedSection.key === "recommendation_matrix") {
                              event.preventDefault();
                              if (draggedGuidedItem && draggedGuidedItem !== item.key) {
                                setDragTargetGuidedItem(item.key);
                              }
                            }
                          }}
                          onDragLeave={() => {
                            if (dragTargetGuidedItem === item.key) {
                              setDragTargetGuidedItem("");
                            }
                          }}
                          onDrop={() => {
                            if (selectedSection.key === "recommendation_matrix") {
                              reorderGuidedListItem(selectedSection.key, draggedGuidedItem, item.key);
                              setDraggedGuidedItem("");
                              setDragTargetGuidedItem("");
                            }
                          }}
                          onDragEnd={() => {
                            setDraggedGuidedItem("");
                            setDragTargetGuidedItem("");
                          }}
                        >
                          <div className="guided-chip-card-top">
                            <span className="guided-chip-step">{`0${itemIndex}`}</span>
                            <span className={`guided-chip-progress guided-chip-progress-${itemTone}`}>{completion}%</span>
                          </div>
                          <strong>{item.label}</strong>
                        </button>
                        );
                        })}
                      </div>
                      <button
                        type="button"
                        className="guided-nav-arrow guided-nav-arrow-rail"
                        onClick={() => shiftGuidedNavWindow(1)}
                        disabled={!canShiftGuidedNavNext}
                        aria-label="Next sub section items"
                      >
                        {">"}
                      </button>
                  </div>

                  {selectedSection.key === "executive_summary" && selectedGuidedConfig.mode === "fixed" ? (
                    <div className="executive-summary-shell">
                      <div className="guided-nav-rail">
                        <button
                          type="button"
                          className="guided-nav-arrow guided-nav-arrow-rail"
                          onClick={() => shiftGuidedNavWindow(-1)}
                          disabled={!canShiftGuidedNavPrev}
                          aria-label="Previous executive summary items"
                        >
                          {"<"}
                        </button>
                        <div className="executive-summary-map">
                          {visibleGuidedNavItems.map((item, index) => {
                          const executiveItem = selectedGuidedState.items?.[item.key] || {};
                          const summaryText = String(executiveItem.summary || "").trim();
                          const instructions = {
                            investigation_summary: "What was checked and what you found.",
                            client_clarification: "What the client should clearly understand.",
                            recommended_direction: "What you recommend doing next.",
                            immediate_actions: "What should happen immediately."
                          };
                          const completionTone = summaryText.length >= 80
                            ? "complete"
                            : summaryText.length > 0
                              ? "partial"
                              : "empty";

                            return (
                              <button
                              key={item.key}
                              type="button"
                              className={`executive-summary-map-item executive-summary-map-item-${completionTone} ${activeGuidedKey === item.key ? "active" : ""}`}
                              onClick={() => setGuidedFocus((prev) => ({ ...prev, [selectedSection.key]: item.key }))}
                            >
                              <div className="executive-summary-map-top">
                                <span className="executive-summary-map-step">{`0${guidedNavOffset + index + 1}`}</span>
                                <span className={`executive-summary-progress executive-summary-progress-${completionTone}`}>
                                  {completionTone === "complete" ? "Complete" : completionTone === "partial" ? "In Progress" : "Empty"}
                                </span>
                              </div>
                              <div className="executive-summary-map-heading">
                                <strong>{item.label}</strong>
                              </div>
                              <p>{instructions[item.key] || "Add a short client-facing point."}</p>
                              </button>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="guided-nav-arrow guided-nav-arrow-rail"
                          onClick={() => shiftGuidedNavWindow(1)}
                          disabled={!canShiftGuidedNavNext}
                          aria-label="Next executive summary items"
                        >
                          {">"}
                        </button>
                      </div>
                      {activeGuidedItem ? (
                        <div className="executive-summary-content">
                          <div className={`guided-card guided-card-${selectedSection.key}`}>
                            <div className={`guided-field-grid guided-field-grid-${selectedSection.key}`}>
                              {selectedGuidedConfig.fields.map((field) => {
                                const fieldBusyGenerate = aiBusyMode === `${selectedSection.key}:${activeGuidedItem.label || activeGuidedKey}:${field.key}:generate`;
                                const fieldBusyRewrite = aiBusyMode === `${selectedSection.key}:${activeGuidedItem.label || activeGuidedKey}:${field.key}:rewrite`;
                                const commonProps = {
                                  value: activeGuidedItem[field.key] || "",
                                  onChange: (event) => updateGuidedField(
                                    selectedSection.key,
                                    selectedGuidedConfig.mode === "list" ? activeGuidedKey : activeGuidedKey,
                                    field.key,
                                    field.type === "number" ? sanitizeRatingValue(event.target.value) : event.target.value
                                  )
                                };

                                return (
                                  <label key={field.key} className={`guided-field guided-field-${field.type}`}>
                                    {field.key !== "summary" ? (
                                      <span>{field.label}</span>
                                    ) : null}
                                    {field.type === "textarea" ? (
                                      <SimpleTextEditor
                                        rows={4}
                                        placeholder="Write one short client-facing sentence."
                                        value={commonProps.value}
                                        onChange={(nextValue) => updateGuidedField(
                                          selectedSection.key,
                                          selectedGuidedConfig.mode === "list" ? activeGuidedKey : activeGuidedKey,
                                          field.key,
                                          nextValue
                                        )}
                                        ariaLabel={`${selectedSection.key}-${activeGuidedItem.label || activeGuidedKey}-${field.key}-editor`}
                                      />
                                    ) : field.type === "select" ? (
                                      <select {...commonProps}>
                                        {(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
                                      </select>
                                    ) : (
                                      <input type={field.type === "number" ? "number" : "text"} placeholder={field.placeholder} {...commonProps} />
                                    )}
                                    {field.type === "textarea" ? (
                                      <AiFieldActions
                                        helper={`Generate or refine "${field.label}"`}
                                        loadingMode={fieldBusyGenerate ? "generate" : fieldBusyRewrite ? "rewrite" : ""}
                                        onGenerate={() => openAiDialog("generate", {
                                          busyKey: `${selectedSection.key}:${activeGuidedItem.label || activeGuidedKey}:${field.key}:generate`,
                                          fieldLabel: `${selectedSection.label} - ${activeGuidedItem.label || activeGuidedKey} - ${field.label}`,
                                          fieldKey: field.key,
                                          subsectionLabel: activeGuidedItem.label || activeGuidedKey,
                                          currentText: activeGuidedItem[field.key] || "",
                                          onResult: (text) => updateGuidedField(selectedSection.key, selectedGuidedConfig.mode === "list" ? activeGuidedKey : activeGuidedKey, field.key, text)
                                        })}
                                        onRewrite={() => openAiDialog("rewrite", {
                                          busyKey: `${selectedSection.key}:${activeGuidedItem.label || activeGuidedKey}:${field.key}:rewrite`,
                                          fieldLabel: `${selectedSection.label} - ${activeGuidedItem.label || activeGuidedKey} - ${field.label}`,
                                          fieldKey: field.key,
                                          subsectionLabel: activeGuidedItem.label || activeGuidedKey,
                                          currentText: activeGuidedItem[field.key] || "",
                                          onResult: (text) => updateGuidedField(selectedSection.key, selectedGuidedConfig.mode === "list" ? activeGuidedKey : activeGuidedKey, field.key, text)
                                        })}
                                      />
                                    ) : null}
                                  </label>
                                );
                              })}
                            </div>
                            {(saveStatus || autosaveStatus) ? (
                              <div className="composer-inline-status">
                                {saveStatus ? <span className="composer-save-status">{saveStatus}</span> : null}
                                {autosaveStatus ? <span className="composer-autosave-status">{autosaveStatus}</span> : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : activeGuidedItem ? (
                    <div className={`guided-card guided-card-${selectedSection.key}`}>
                      <div className="guided-card-head">
                        <div className="guided-card-title-wrap">
                          {selectedGuidedConfig.mode === "list" ? (
                            <label className="guided-label-editor">
                              <span>Item Heading</span>
                              <input
                                type="text"
                                value={activeGuidedItem.label || ""}
                                placeholder={`${selectedGuidedConfig.itemLabel} title`}
                                onChange={(event) => {
                                  const nextLabel = event.target.value;
                                  const currentIndex = parseListItemIndex(activeGuidedKey);
                                  const nextItems = selectedGuidedState.items.map((item, index) =>
                                    index === currentIndex ? { ...item, label: nextLabel } : item
                                  );
                                  setGuidedFocus((prev) => ({ ...prev, [selectedSection.key]: activeGuidedKey }));
                                  setSectionText(selectedSection.key, serializeGuidedSection(selectedGuidedConfig, { items: nextItems }));
                                  syncRecommendationMatrixMeta(selectedSection.key, nextItems);
                                }}
                              />
                            </label>
                          ) : (
                            <strong>{activeGuidedItem.label || activeGuidedKey}</strong>
                          )}
                        </div>
                        <div className="guided-card-actions">
                          {selectedGuidedConfig.mode === "list" && guidedNavItems.length > 1 ? (
                            <>
                              {selectedSection.key === "recommendation_matrix" ? (
                                <>
                                  <button
                                    type="button"
                                    className="button-link ghost compact-action-btn"
                                    onClick={() => moveGuidedListItem(selectedSection.key, activeGuidedKey, "up")}
                                  >
                                    Move Up
                                  </button>
                                  <button
                                    type="button"
                                    className="button-link ghost compact-action-btn"
                                    onClick={() => moveGuidedListItem(selectedSection.key, activeGuidedKey, "down")}
                                  >
                                    Move Down
                                  </button>
                                </>
                              ) : null}
                              <button
                                type="button"
                                className="button-link ghost compact-action-btn"
                                onClick={() => removeGuidedListItem(selectedSection.key, activeGuidedKey)}
                              >
                                Remove
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                      {selectedSection.key === "recommendation_matrix" ? (
                        <div className="guided-row-summary">
                          <span className="outline-status-chip outline-status-low">{activeGuidedItem.area || "Area not set"}</span>
                          <span className="outline-status-chip outline-status-medium">{activeGuidedItem.priority || "Priority not set"}</span>
                          <span className="outline-status-chip outline-status-neutral">{activeGuidedItem.impact || "Impact not set"}</span>
                          <span className="outline-status-chip outline-status-neutral">{activeGuidedItem.effort || "Effort not set"}</span>
                          {activeGuidedItem.suggested_owner ? <span className="outline-status-chip outline-status-low">{activeGuidedItem.suggested_owner}</span> : null}
                        </div>
                      ) : null}
                      {selectedSection.key === "current_risks" ? (
                        <div className="guided-row-summary">
                          <span className="outline-status-chip outline-status-high">{activeGuidedItem.area || "Area not set"}</span>
                          <span className="outline-status-chip outline-status-medium">{activeGuidedItem.status || "Status not set"}</span>
                          <span className={`outline-score-chip outline-score-${deriveScoreTone(Number(activeGuidedItem.rating || 0))}`}>
                            {activeGuidedItem.rating ? `${activeGuidedItem.rating}/100` : "No rating"}
                          </span>
                        </div>
                      ) : null}
                      {selectedSection.key === "positive_signals" ? (
                        <div className="guided-row-summary">
                          <span className="outline-status-chip outline-status-low">{activeGuidedItem.area || "Area not set"}</span>
                          <span className="outline-status-chip outline-status-low">{activeGuidedItem.status || "Status not set"}</span>
                          <span className={`outline-score-chip outline-score-${deriveScoreTone(Number(activeGuidedItem.rating || 0))}`}>
                            {activeGuidedItem.rating ? `${activeGuidedItem.rating}/100` : "No rating"}
                          </span>
                        </div>
                      ) : null}
                      {selectedSection.key === "operational_readiness" ? (
                        <div className="guided-row-summary">
                          <span className="outline-status-chip outline-status-medium">{activeGuidedItem.status || "Status not set"}</span>
                          <span className={`outline-score-chip outline-score-${deriveScoreTone(Number(activeGuidedItem.rating || 0))}`}>
                            {activeGuidedItem.rating ? `${activeGuidedItem.rating}/100` : "No rating"}
                          </span>
                          {activeGuidedItem.gap ? <span className="outline-status-chip outline-status-high">Gap Noted</span> : null}
                          {activeGuidedItem.recommendation ? <span className="outline-status-chip outline-status-low">Recommendation Added</span> : null}
                        </div>
                      ) : null}
                      {selectedSection.key === "documentation_and_handover" ? (
                        <div className="guided-row-summary">
                          <span className="outline-status-chip outline-status-medium">{activeGuidedItem.doc_type || "Type not set"}</span>
                          <span className="outline-status-chip outline-status-medium">{activeGuidedItem.status || "Status not set"}</span>
                          <span className={`outline-score-chip outline-score-${deriveScoreTone(Number(activeGuidedItem.rating || 0))}`}>
                            {activeGuidedItem.rating ? `${activeGuidedItem.rating}/100` : "No rating"}
                          </span>
                          {activeGuidedItem.owner ? <span className="outline-status-chip outline-status-low">{activeGuidedItem.owner}</span> : null}
                          {activeGuidedItem.storage_location ? <span className="outline-status-chip outline-status-low">Location Captured</span> : null}
                          {activeGuidedItem.gap ? <span className="outline-status-chip outline-status-high">Gap Noted</span> : null}
                        </div>
                      ) : null}
                      <div className={`guided-field-grid guided-field-grid-${selectedSection.key}`}>
                        {selectedGuidedConfig.fields.map((field) => {
                          const fieldBusyGenerate = aiBusyMode === `${selectedSection.key}:${activeGuidedItem.label || activeGuidedKey}:${field.key}:generate`;
                          const fieldBusyRewrite = aiBusyMode === `${selectedSection.key}:${activeGuidedItem.label || activeGuidedKey}:${field.key}:rewrite`;
                          const commonProps = {
                            value: activeGuidedItem[field.key] || "",
                            onChange: (event) => updateGuidedField(
                              selectedSection.key,
                              selectedGuidedConfig.mode === "list" ? activeGuidedKey : activeGuidedKey,
                              field.key,
                              field.type === "number" ? sanitizeRatingValue(event.target.value) : event.target.value
                            )
                          };

                          return (
                            <label key={field.key} className={`guided-field guided-field-${field.type}`}>
                              {true ? (
                                <span>{field.label}</span>
                              ) : null}
                              {field.type === "textarea" ? (
                                <SimpleTextEditor
                                  rows={4}
                                  placeholder={field.placeholder}
                                  value={commonProps.value}
                                  onChange={(nextValue) => updateGuidedField(
                                    selectedSection.key,
                                    selectedGuidedConfig.mode === "list" ? activeGuidedKey : activeGuidedKey,
                                    field.key,
                                    nextValue
                                  )}
                                  ariaLabel={`${selectedSection.key}-${activeGuidedItem.label || activeGuidedKey}-${field.key}-editor`}
                                />
                              ) : field.type === "select" ? (
                                <select {...commonProps}>
                                  {(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
                                </select>
                              ) : (
                                <input type={field.type === "number" ? "number" : "text"} placeholder={field.placeholder} {...commonProps} />
                              )}
                              {field.type === "textarea" ? (
                                <AiFieldActions
                                  helper={`Generate or refine "${field.label}"`}
                                  loadingMode={fieldBusyGenerate ? "generate" : fieldBusyRewrite ? "rewrite" : ""}
                                  onGenerate={() => openAiDialog("generate", {
                                    busyKey: `${selectedSection.key}:${activeGuidedItem.label || activeGuidedKey}:${field.key}:generate`,
                                    fieldLabel: `${selectedSection.label} - ${activeGuidedItem.label || activeGuidedKey} - ${field.label}`,
                                    fieldKey: field.key,
                                    subsectionLabel: activeGuidedItem.label || activeGuidedKey,
                                    currentText: activeGuidedItem[field.key] || "",
                                    onResult: (text) => updateGuidedField(selectedSection.key, selectedGuidedConfig.mode === "list" ? activeGuidedKey : activeGuidedKey, field.key, text)
                                  })}
                                  onRewrite={() => openAiDialog("rewrite", {
                                    busyKey: `${selectedSection.key}:${activeGuidedItem.label || activeGuidedKey}:${field.key}:rewrite`,
                                    fieldLabel: `${selectedSection.label} - ${activeGuidedItem.label || activeGuidedKey} - ${field.label}`,
                                    fieldKey: field.key,
                                    subsectionLabel: activeGuidedItem.label || activeGuidedKey,
                                    currentText: activeGuidedItem[field.key] || "",
                                    onResult: (text) => updateGuidedField(selectedSection.key, selectedGuidedConfig.mode === "list" ? activeGuidedKey : activeGuidedKey, field.key, text)
                                  })}
                                />
                              ) : null}
                            </label>
                          );
                        })}
                      </div>
                      {(activeGuidedMeta.rating !== undefined || activeGuidedMeta.status) ? (
                        <div className="guided-meta-summary">
                          <span>Stored Rating: <strong>{activeGuidedMeta.rating === "" || activeGuidedMeta.rating === undefined ? "-" : activeGuidedMeta.rating}</strong></span>
                          <span>Stored Status: <strong>{activeGuidedMeta.status || "-"}</strong></span>
                        </div>
                      ) : null}
                      {(saveStatus || autosaveStatus) ? (
                        <div className="composer-inline-status">
                          {saveStatus ? <span className="composer-save-status">{saveStatus}</span> : null}
                          {autosaveStatus ? <span className="composer-autosave-status">{autosaveStatus}</span> : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </section>
              ) : null}
              {!selectedGuidedConfig ? (
                <>
                  {(saveStatus || autosaveStatus) ? (
                    <div className="composer-inline-status">
                      {saveStatus ? <span className="composer-save-status">{saveStatus}</span> : null}
                      {autosaveStatus ? <span className="composer-autosave-status">{autosaveStatus}</span> : null}
                    </div>
                  ) : null}
                  <SimpleTextEditor
                    rows={10}
                    value={sections[selectedSection.key] || ""}
                    onChange={(nextValue) => setSections((prev) => ({ ...prev, [selectedSection.key]: nextValue }))}
                    placeholder={`Write ${selectedSection.label.toLowerCase()}...`}
                    ariaLabel={`${selectedSection.key}-editor`}
                    minHeight="220px"
                  />
                  <AiFieldActions
                    helper="Draft or refine this report section"
                    loadingMode={aiBusyMode}
                    onGenerate={() => openAiDialog("generate")}
                    onRewrite={() => openAiDialog("rewrite")}
                  />
                </>
              ) : null}
            </section>

            <section className="report-submit-box">
              <h4>Finalize and Share</h4>
              <p className="muted">Generate one client-safe report URL and keep using the same link on future updates. Only the secret code needs to change when you want to rotate access.</p>
              <input
                value={secretCode}
                onChange={(event) => setSecretCode(event.target.value)}
                placeholder={isReportGenerated ? "Enter new secret code only if you want to change it" : "Optional secret code"}
              />
              <button type="button" onClick={submitDraft} disabled={submitting || workspaceId <= 0}>
                {submitting ? (isReportGenerated ? "Updating..." : "Generating...") : (isReportGenerated ? "Update Report" : "Submit and Generate Report")}
              </button>
              {saveStatus ? <p className="status-text">{saveStatus}</p> : null}

              {publicFrontendUrl ? (
                <div className="report-submit-result">
                  <p className="muted">Client access is active for this report. Use the actions below when you need to share or open it.</p>
                  <div className="hero-actions">
                    <button type="button" className="button-link ghost compact-action-btn" onClick={copyShareAccess}>Copy Access</button>
                    <button type="button" className="button-link ghost compact-action-btn" onClick={shareByEmail}>Email Access</button>
                    <button type="button" className="button-link ghost compact-action-btn" onClick={shareAccess}>Share</button>
                    <a className="button-link ghost compact-action-btn" href={publicFrontendUrl} target="_blank" rel="noreferrer">Open Report</a>
                  </div>
                </div>
              ) : null}
            </section>
          </section>
        </section>
      ) : (
        <section className="card-modern reports-evidence-hub">
          {loading ? <p className="muted">Loading evidence...</p> : null}
          <section className="evidence-toolbar">
            <div>
              <h4>Evidence Hub</h4>
              <p className="muted">Use only the evidence you need. Add it directly into the most relevant report section without re-reading the full questionnaire here.</p>
            </div>
            <div className="tab-row">
              <button className={`tab-btn ${evidenceFilter === "all" ? "active" : ""}`} onClick={() => setEvidenceFilter("all")}>All</button>
              <button className={`tab-btn ${evidenceFilter === "questionnaire" ? "active" : ""}`} onClick={() => setEvidenceFilter("questionnaire")}>Answers</button>
              <button className={`tab-btn ${evidenceFilter === "link" ? "active" : ""}`} onClick={() => setEvidenceFilter("link")}>Answer Links</button>
              <button className={`tab-btn ${evidenceFilter === "incident" ? "active" : ""}`} onClick={() => setEvidenceFilter("incident")}>Incidents</button>
              <button className={`tab-btn ${evidenceFilter === "attachment" ? "active" : ""}`} onClick={() => setEvidenceFilter("attachment")}>Attachments</button>
            </div>
            <input
              value={evidenceSearch}
              onChange={(event) => setEvidenceSearch(event.target.value)}
              placeholder="Search evidence..."
            />
          </section>

          <div className="evidence-section evidence-stream">
            {filteredEvidence.map((item) => (
              <article key={item.id} className="evidence-card evidence-card-rich">
                <div className="section-head">
                  <div>
                    <p className="tool-permalink">{item.type}</p>
                    <h5>{item.title}</h5>
                  </div>
                </div>
                <p>{item.subtitle}</p>
                {item.type === "incident" ? (
                  <>
                    <p><strong>Issue:</strong> {item.payload.issue_summary || item.payload.details || "-"}</p>
                    <p><strong>Root Cause:</strong> {item.payload.root_cause || "-"}</p>
                    <p><strong>Business Impact:</strong> {item.payload.business_impact || "-"}</p>
                  </>
                ) : null}
                {renderEvidenceActions(item)}
              </article>
            ))}
            {!filteredEvidence.length ? <p className="muted">No evidence matches this filter yet.</p> : null}
          </div>
        </section>
      )}
      <NotesIngestModal
        open={notesDialogOpen}
        value={notesText}
        onChange={(value) => setNotesText(value)}
        onClose={() => {
          if (!notesBusy) {
            setNotesDialogOpen(false);
            setNotesError("");
          }
        }}
        onSubmit={ingestNotes}
        loading={notesBusy}
        error={notesError}
      />
      <AiAssistOptionsModal
        open={Boolean(aiDialog)}
        mode={aiDialog?.mode || "generate"}
        onClose={() => setAiDialog(null)}
        onSubmit={applyAiDialog}
      />
    </main>
  );
}

